import { useEffect, useState, useCallback, useRef } from 'react';
import { debounce } from 'es-toolkit';
import { useSearchParams } from 'react-router-dom';
import { useAuditLogs } from '../hooks/useAuditLogs';
import { useOffsetSortPagination } from '../hooks/useOffsetSortPagination';
import Card from '../components/Card';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import AuditDetailModal from '../components/AuditDetailModal';
import { Link } from 'react-router-dom';
import {
  applyListTableToSearchParams,
  initialSortingFromListUrl,
  readAuditFilterParams,
  readListTableSearch,
  searchParamsEffectivelyEqual,
  serverPaginationInitFromListUrl,
} from '../lib/listUrlParams';
import type { AuditLog } from '../lib/types';
import Button from '../components/Button';
import Skeleton from '../components/Skeleton';

function formatDate(dateString?: string): string {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return dateString;
  }
}

function getEntityLink(log: AuditLog): string | null {
  if (log.entityType === 'PACK') {
    return `/packs/${log.entityId}`;
  } else if (log.entityType === 'CELL') {
    return `/cells/${log.entityId}`;
  }
  return null;
}

function getActionBadgeColor(action: string): string {
  switch (action) {
    case 'CREATE':
      return 'bg-green-100 text-green-800';
    case 'UPDATE':
      return 'bg-blue-100 text-blue-800';
    case 'DELETE':
      return 'bg-red-100 text-red-800';
    case 'SOFT_DELETE':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export default function AuditLogsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlBootRef = useRef<{
    filters: ReturnType<typeof readAuditFilterParams>;
    table: ReturnType<typeof readListTableSearch>;
  } | null>(null);
  if (urlBootRef.current === null) {
    const sp = new URLSearchParams(window.location.search);
    urlBootRef.current = {
      filters: readAuditFilterParams(sp),
      table: readListTableSearch(sp),
    };
  }
  const urlBoot = urlBootRef.current;
  const [entityType, setEntityType] = useState<'PACK' | 'CELL' | 'CUSTOMER' | 'TEST' | ''>(
    urlBoot.filters.entityType
  );
  const [entityId, setEntityId] = useState(urlBoot.filters.entityId);
  const [localEntityId, setLocalEntityId] = useState(urlBoot.filters.entityId);
  const [action, setAction] = useState<'CREATE' | 'UPDATE' | 'DELETE' | 'SOFT_DELETE' | ''>(
    urlBoot.filters.action
  );
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    pagination,
    sorting,
    listQuery,
    onSortingChange,
    onPageSizeChange,
    clearSorting,
    syncCursorLastKey,
    canGoNext,
  } = useOffsetSortPagination(20, {
    initialSorting: initialSortingFromListUrl(urlBoot.table.sortId, urlBoot.table.orderDesc),
    paginationInit: serverPaginationInitFromListUrl(
      urlBoot.table.page,
      urlBoot.table.sortId,
      urlBoot.table.cursors
    ),
  });

  const debouncedSetEntityId = useCallback(
    debounce((value: string) => {
      setEntityId(value);
    }, 300),
    []
  );

  const { data, isLoading, error } = useAuditLogs({
    entityType: entityType || undefined,
    entityId: entityId || undefined,
    action: action || undefined,
    limit: pagination.pageSize,
    ...listQuery,
  });

  useEffect(() => {
    syncCursorLastKey(data?.lastKey);
  }, [data?.lastKey, syncCursorLastKey]);

  useEffect(() => {
    pagination.reset();
    clearSorting();
  }, [entityType, entityId, action, pagination.reset, clearSorting]);

  useEffect(() => {
    const next = applyListTableToSearchParams(searchParams, {
      page: pagination.currentPage,
      sorting,
      lastKeys: pagination.lastKeys,
    });
    if (entityType) next.set('entityType', entityType);
    else next.delete('entityType');
    if (entityId) next.set('entityId', entityId);
    else next.delete('entityId');
    if (action) next.set('action', action);
    else next.delete('action');
    if (!searchParamsEffectivelyEqual(next, searchParams)) {
      setSearchParams(next, { replace: true });
    }
  }, [
    searchParams,
    setSearchParams,
    pagination.currentPage,
    pagination.lastKeys,
    sorting,
    entityType,
    entityId,
    action,
  ]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Card className="p-0 overflow-hidden">
          <div className="space-y-4 p-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex gap-4">
                <Skeleton className="h-10 w-full" />
              </div>
            ))}
          </div>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <div className="bg-red-50 border-l-4 border-red-500 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-red-900 mb-1">Error Loading Data</h4>
              <p className="text-sm text-red-700">
                {error instanceof Error ? error.message : 'Failed to load audit logs.'}
              </p>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const logs = data?.items || [];
  const totalPages = data?.total ? Math.ceil(data.total / pagination.pageSize) : null;

  const columns = [
    {
      header: 'Timestamp',
      accessor: 'timestamp' as keyof AuditLog,
      mobileLabel: 'Timestamp',
      enableSorting: true,
      cellRenderer: (log: AuditLog) => (
        <span className="text-sm text-gray-600 font-medium">{formatDate(log.timestamp || log.created_at)}</span>
      ),
    },
    {
      header: 'Entity Type',
      accessor: 'entityType' as keyof AuditLog,
      mobileLabel: 'Entity Type',
      enableSorting: true,
      cellRenderer: (log: AuditLog) => (
        <span className="font-medium text-gray-900">{log.entityType}</span>
      ),
    },
    {
      header: 'Entity ID',
      accessor: 'entityId' as keyof AuditLog,
      mobileLabel: 'Entity ID',
      enableSorting: true,
      cellRenderer: (log: AuditLog) => {
        const link = getEntityLink(log);
        if (link) {
          return (
            <Link
              to={link}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              {log.entityId}
            </Link>
          );
        }
        return <span className="font-medium">{log.entityId}</span>;
      },
    },
    {
      header: 'Action',
      accessor: 'action' as keyof AuditLog,
      mobileLabel: 'Action',
      enableSorting: true,
      cellRenderer: (log: AuditLog) => (
        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getActionBadgeColor(log.action)}`}>
          {log.action}
        </span>
      ),
    },
    {
      header: 'User',
      accessor: 'userId' as keyof AuditLog,
      mobileLabel: 'User',
      enableSorting: true,
      cellRenderer: (log: AuditLog) => (
        <span className="text-gray-600">
          {log.userName || log.userId || 'N/A'}
        </span>
      ),
    },
  ];

  const handlePageChange = (direction: 'next' | 'prev' | 'first' | 'last') => {
    if (direction === 'next') {
      pagination.nextPage();
    } else if (direction === 'prev') {
      pagination.previousPage();
    } else if (direction === 'first') {
      pagination.reset();
    }
  };

  const handleClearFilters = () => {
    setEntityType('');
    setEntityId('');
    setLocalEntityId('');
    setAction('');
    pagination.reset();
    clearSorting();
  };

  const handleRowClick = (log: AuditLog) => {
    setSelectedLog(log);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLog(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Filters</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity Type
              </label>
              <select
                value={entityType}
                onChange={(e) => setEntityType(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                <option value="PACK">Pack</option>
                <option value="CELL">Cell</option>
                <option value="CUSTOMER">Customer</option>
                <option value="TEST">Test</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Entity ID
              </label>
              <input
                type="text"
                value={localEntityId}
                onChange={(e) => {
                  setLocalEntityId(e.target.value);
                  debouncedSetEntityId(e.target.value);
                }}
                placeholder="e.g., PACK-001"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Action
              </label>
              <select
                value={action}
                onChange={(e) => setAction(e.target.value as any)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Actions</option>
                <option value="CREATE">Create</option>
                <option value="UPDATE">Update</option>
                <option value="DELETE">Delete</option>
                <option value="SOFT_DELETE">Soft Delete</option>
              </select>
            </div>
            <div className="flex items-end">
              <Button
                variant="secondary"
                onClick={handleClearFilters}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <Card>
        <Table
          columns={columns}
          data={logs}
          onRowClick={handleRowClick}
          emptyMessage="No audit logs found"
          enableSorting={true}
          enablePagination={false}
          total={data?.total ?? null}
          sorting={sorting}
          onSortingChange={onSortingChange}
        />

        {logs.length > 0 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={totalPages}
            total={data?.total ?? null}
            pageSize={pagination.pageSize}
            canGoNext={canGoNext(data?.total ?? 0, data?.lastKey)}
            canGoPrevious={pagination.canGoPrevious}
            onPageChange={handlePageChange}
            onPageSizeChange={onPageSizeChange}
          />
        )}
      </Card>

      {selectedLog && (
        <AuditDetailModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          auditLog={selectedLog}
        />
      )}
    </div>
  );
}
